/**
 * Medication Reminder Service
 *
 * CRUD operations for medication reminders with support for
 * interval-based and fixed-time scheduling, pause/resume, and partner sync.
 */

import { supabase } from '@/lib/supabase';

// Types
export type ScheduleMode = 'interval' | 'fixed_times';

export interface MedicationReminder {
  id: string;
  pregnancy_id: string;
  created_by: string;
  baby_index: number;
  medication_name: string;
  medication_dose: string | null;
  schedule_mode: ScheduleMode;
  interval_hours: number | null;
  fixed_times: string[]; // ["08:00", "14:00", "20:00"]
  start_date: string;
  end_date: string | null;
  is_active: boolean;
  is_paused: boolean;
  next_reminder_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateMedicationReminder {
  pregnancy_id: string;
  baby_index?: number;
  medication_name: string;
  medication_dose?: string;
  schedule_mode: ScheduleMode;
  interval_hours?: number;
  fixed_times?: string[];
  start_date?: string;
  end_date?: string | null;
  notes?: string;
}

export interface UpdateMedicationReminder {
  medication_name?: string;
  medication_dose?: string | null;
  schedule_mode?: ScheduleMode;
  interval_hours?: number | null;
  fixed_times?: string[];
  start_date?: string;
  end_date?: string | null;
  is_paused?: boolean;
  notes?: string | null;
}

/**
 * Get all medication reminders for a pregnancy
 */
export async function getMedicationReminders(
  pregnancyId: string
): Promise<{ data: MedicationReminder[]; error: string | null }> {
  const { data, error } = await supabase
    .from('medication_reminders')
    .select('*')
    .eq('pregnancy_id', pregnancyId)
    .eq('is_active', true)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching medication reminders:', error);
    return { data: [], error: error.message };
  }

  return { data: data || [], error: null };
}

/**
 * Get a single medication reminder by ID
 */
export async function getMedicationReminder(
  reminderId: string
): Promise<{ data: MedicationReminder | null; error: string | null }> {
  const { data, error } = await supabase
    .from('medication_reminders')
    .select('*')
    .eq('id', reminderId)
    .single();

  if (error) {
    console.error('Error fetching medication reminder:', error);
    return { data: null, error: error.message };
  }

  return { data, error: null };
}

/**
 * Create a new medication reminder
 */
export async function createMedicationReminder(
  userId: string,
  reminder: CreateMedicationReminder
): Promise<{ data: MedicationReminder | null; error: string | null }> {
  const { data, error } = await supabase
    .from('medication_reminders')
    .insert({
      pregnancy_id: reminder.pregnancy_id,
      created_by: userId,
      baby_index: reminder.baby_index ?? 0,
      medication_name: reminder.medication_name,
      medication_dose: reminder.medication_dose || null,
      schedule_mode: reminder.schedule_mode,
      interval_hours: reminder.schedule_mode === 'interval' ? reminder.interval_hours : null,
      fixed_times: reminder.schedule_mode === 'fixed_times' ? (reminder.fixed_times || []) : [],
      start_date: reminder.start_date || new Date().toISOString().split('T')[0],
      end_date: reminder.end_date || null,
      notes: reminder.notes || null,
      is_active: true,
      is_paused: false,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating medication reminder:', error);
    return { data: null, error: error.message };
  }

  return { data, error: null };
}

/**
 * Update an existing medication reminder
 */
export async function updateMedicationReminder(
  reminderId: string,
  updates: UpdateMedicationReminder
): Promise<{ data: MedicationReminder | null; error: string | null }> {
  // Build update object, only including provided fields
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateObj: any = { updated_at: new Date().toISOString() };

  if (updates.medication_name !== undefined) updateObj.medication_name = updates.medication_name;
  if (updates.medication_dose !== undefined) updateObj.medication_dose = updates.medication_dose;
  if (updates.schedule_mode !== undefined) updateObj.schedule_mode = updates.schedule_mode;
  if (updates.interval_hours !== undefined) updateObj.interval_hours = updates.interval_hours;
  if (updates.fixed_times !== undefined) updateObj.fixed_times = updates.fixed_times;
  if (updates.start_date !== undefined) updateObj.start_date = updates.start_date;
  if (updates.end_date !== undefined) updateObj.end_date = updates.end_date;
  if (updates.is_paused !== undefined) updateObj.is_paused = updates.is_paused;
  if (updates.notes !== undefined) updateObj.notes = updates.notes;

  const { data, error } = await supabase
    .from('medication_reminders')
    .update(updateObj)
    .eq('id', reminderId)
    .select()
    .single();

  if (error) {
    console.error('Error updating medication reminder:', error);
    return { data: null, error: error.message };
  }

  return { data, error: null };
}

/**
 * Pause or resume a medication reminder
 */
export async function toggleMedicationReminderPause(
  reminderId: string,
  isPaused: boolean
): Promise<{ data: MedicationReminder | null; error: string | null }> {
  return updateMedicationReminder(reminderId, { is_paused: isPaused });
}

/**
 * Delete (soft-delete by deactivating) a medication reminder
 */
export async function deleteMedicationReminder(
  reminderId: string
): Promise<{ success: boolean; error: string | null }> {
  const { error } = await supabase
    .from('medication_reminders')
    .update({ is_active: false, next_reminder_at: null, updated_at: new Date().toISOString() })
    .eq('id', reminderId);

  if (error) {
    console.error('Error deleting medication reminder:', error);
    return { success: false, error: error.message };
  }

  return { success: true, error: null };
}

// ── Medication Reminder Log ──────────────────────────────────

export interface PendingReminderLog {
  id: string;
  reminder_id: string;
  scheduled_for: string;
}

/**
 * Get pending (unacknowledged) reminder log entries.
 * These are reminders that the cron sent but the user hasn't confirmed or dismissed.
 */
export async function getPendingReminderLogs(
  pregnancyId: string
): Promise<{ data: PendingReminderLog[]; error: string | null }> {
  const { data, error } = await supabase
    .from('medication_reminder_log')
    .select('id, reminder_id, scheduled_for')
    .eq('pregnancy_id', pregnancyId)
    .is('was_given', null)
    .order('scheduled_for', { ascending: false });

  if (error) {
    console.error('Error fetching pending reminder logs:', error);
    return { data: [], error: error.message };
  }

  return { data: data || [], error: null };
}

/**
 * Acknowledge a reminder log entry (confirm medication given or dismiss).
 */
export async function acknowledgeReminderLog(
  logId: string,
  wasGiven: boolean,
  givenAt?: string
): Promise<{ success: boolean; error: string | null }> {
  const { error } = await supabase
    .from('medication_reminder_log')
    .update({
      was_given: wasGiven,
      given_at: wasGiven ? (givenAt || new Date().toISOString()) : null,
    })
    .eq('id', logId);

  if (error) {
    console.error('Error acknowledging reminder log:', error);
    return { success: false, error: error.message };
  }

  return { success: true, error: null };
}
